import React, { useState, useEffect } from 'react';
import { Table, Button } from 'react-bootstrap';

import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceArea, ResponsiveContainer,
} from 'recharts';

function RainfallChart({ data, defaultView = '24h', onSelectionSumChange = function() {} }) {
  const [viewMode, setViewMode] = useState(defaultView); // 'all' or '24h'
  const [current24hStart, setCurrent24hStart] = useState(new Date((new Date(data[data.length - 1]?.entryTimestamp)).getTime() - (24 * 60 * 60 * 1000))); // Start of the current 24-hour window
  const [dragStart, setDragStart] = useState(null);
  const [dragEnd, setDragEnd] = useState(null);
  const [selectMode, setSelectMode] = useState(false);
  const [max, setMax] = useState(null);
  const [maxTimeFormatted, setMaxTimeFormatted] = useState(null);
  const [firstEntryTimeFormatted, setFirstEntryTimeFormatted] = useState(null);
  const [lastEntryTimeFormatted, setLastEntryTimeFormatted] = useState(null);
  const [formattedSelectionSum, setFormattedSelectionSum] = useState(null); // State to store formatted sum

  // Normalize the data: Convert nulls to 0
  const normalizedData = data ? data.map(item => ({
    ...item,
    entryTotal: item.entryTotal === null ? 0 : parseFloat(item.entryTotal),
  })) : null;

  // Filter the data for 24-hour view
  const filtered24hData = normalizedData.filter(item => {
    const entryTime = new Date(item.entryTimestamp);
    return entryTime >= current24hStart && entryTime < new Date(current24hStart.getTime() + 24 * 60 * 60 * 1000);
  });

  useEffect(() => {
    if (dragStart && dragEnd) {
      const dragStartIx = dragStart ? normalizedData.findIndex(item => item.entryTimestampFormatted === dragStart) : -1;
      const dragEndIx = dragEnd ? normalizedData.findIndex(item => item.entryTimestampFormatted === dragEnd) : -1;

      const startIx = Math.min(dragStartIx, dragEndIx);
      const endIx = Math.max(dragStartIx, dragEndIx);

      let maxVal = 0.00;
      let maxTime = null;
      let firstEntryTime = null;
      let lastEntryTime = null;

      let selectionSum = null;
      if (startIx !== -1 && endIx !== -1) {
        // Slice the array between startIx and endIx (inclusive)
        const slicedData = normalizedData.slice(startIx, endIx + 1);

        // Sum the entryTotal values in the sliced data and find the max
        selectionSum = slicedData.reduce(function(sum, item) {
          if (item.entryTotal > maxVal) {
            maxVal = item.entryTotal;
            maxTime = item.entryTimestampFormatted;
          }
          return sum + item.entryTotal;
        }, 0);

        firstEntryTime = slicedData.length > 0 ? slicedData[0].entryTimestampFormatted : null;
        lastEntryTime = slicedData.length > 0 ? slicedData[slicedData.length - 1].entryTimestampFormatted : null;
      }

      // Format the selection sum
      const formattedSum = (selectionSum ? selectionSum : 0.00).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      
      // Update the state with the formatted sum, max, and timestamps
      setFormattedSelectionSum(formattedSum);
      setMax(maxVal);
      setMaxTimeFormatted(maxTime);
      setFirstEntryTimeFormatted(firstEntryTime);
      setLastEntryTimeFormatted(lastEntryTime);

      // Propagate the sum to the parent component via the callback
      if (onSelectionSumChange) {
        onSelectionSumChange({
          selectionSum,
          max: maxVal,
          maxTimeFormatted: maxTime,
          firstEntryTimeFormatted: firstEntryTime,
          lastEntryTimeFormatted: lastEntryTime
        });
      }
    }
  }, [dragStart, dragEnd, normalizedData, onSelectionSumChange]);

  useEffect(() => {
  	clearSelection();
  }, [viewMode])

  useEffect(() => {
  const handleEscKeyPress = (event) => {
    if (event.key === 'Escape') {
      console.log('Esc key pressed');
      clearSelection();
      // Perform the action you want when Esc is pressed
    }
  };

  document.addEventListener('keydown', handleEscKeyPress);

  // Cleanup function to remove the event listener
  return () => {
  	clearSelection();
    document.removeEventListener('keydown', handleEscKeyPress);
  };
}, []);

  if (!data || !Array.isArray(data)) {
    return <span>Unable to display rainfall chart</span>;
  }

  // Handle view mode switch and data display
  const chartData = viewMode === 'all' ? normalizedData : filtered24hData;

  const handleStart = (e) => {
  	if(!e) {
  		return;
  	}
  let activeLabel;

  // Check if it's a touch event or a mouse event
  if (e.touches && e.touches.length > 0) {
    activeLabel = e.touches[0].clientX; // For touch, we use the first touch point
    e.preventDefault();
  } else {
    activeLabel = e.activeLabel; // For mouse, we use the provided activeLabel
  }

  if (!activeLabel) return;

  if (!selectMode) {
    setDragStart(null);
    setDragEnd(null); // Reset the selection
  }
  if (dragStart === null || !selectMode) {
    setDragStart(activeLabel);
    setSelectMode(true);
  } else {
    setDragEnd(activeLabel);
    setSelectMode(false);
  }
};

const handleMove = (e) => {
  	if(!e) {
  		return;
  	}
  let activeLabel;

  // Check if it's a touch event or a mouse event
  if (e.touches && e.touches.length > 0) {
    activeLabel = e.touches[0].clientX; // For touch, we use the first touch point
    e.preventDefault();
  } else {
    activeLabel = e.activeLabel; // For mouse, we use the provided activeLabel
  }

  if (!activeLabel) return;

  if (selectMode) {
    setDragEnd(activeLabel);
  }
};

  const handleMouseUp = () => {
    if (dragStart && dragEnd) {
      console.log(`Selected range: ${dragStart} to ${dragEnd}`);
    }
  };

  const clearSelection = () => {
  	setDragEnd(null);
  	setDragStart(null);
  	setSelectMode(false);
  	setMax(null);
  };

  return (
    <div style={{ position: 'relative' }}>
      <div>
        <Button size="sm" variant={viewMode === 'all' ? "secondary" : "dark"} onClick={() => setViewMode('all')}>View 7d</Button><span> </span>
        <Button size="sm" variant={viewMode === '24h' ? "secondary" : "dark"} onClick={() => setViewMode('24h')}>Last 24h</Button>
      </div>

      {/* Mini table displayed in the top right corner */}
      
  <div style={{
    position: 'absolute',
    top: '10px',
    right: '20px',
    backgroundColor: 'black',
    color: 'white',
    padding: '10px',
    borderRadius: '5px',
    fontSize: '12px',
    zIndex: "9999999"
  }}>
    <Table bordered size="sm" variant="dark" style={{ marginBottom: '0' }}>
      <thead>
        <tr>
          <th colSpan="2" style={{ textAlign: 'center', borderBottom: "1px dashed white" }}>
            <strong>{max !== null ? "Selection" : "Chart Total"}</strong>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><strong>Total</strong></td>
          <td>{max !== null ? formattedSelectionSum : chartData.reduce(function(sum, item) {
          	return sum + item.entryTotal;
          }, 0.00).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} mm</td>
        </tr>
        {max !== null ? [<tr>
          <td><strong>Start Time:</strong></td>
          <td>{firstEntryTimeFormatted}</td>
        </tr>,
        <tr>
          <td><strong>End Time:</strong></td>
          <td>{lastEntryTimeFormatted}</td>
        </tr>,
        <tr>
          <td><strong>Max:</strong></td>
          <td>{max.toFixed(2)} mm</td>
        </tr>,
        <tr>
          <td><strong>Max Time:</strong></td>
          <td>{maxTimeFormatted ? maxTimeFormatted : "N/A"}</td>
        </tr>,
        <tr>
          <td colSpan="2" style={{ textAlign: 'center', borderTop: "1px dashed white", paddingTop: "3px" }}>
            <Button variant="secondary" size="sm" onClick={clearSelection}>Clear<span className="d-none d-md-inline"> (Esc Key)</span></Button>
          </td>
        </tr>] : null}
      </tbody>
    </Table>
  </div>


      <ResponsiveContainer width="100%" height={400}>
        <BarChart
          data={chartData}
          margin={{
            top: 10, right: 30, left: 0, bottom: 0,
          }}
           onClick={handleStart}
		    onMouseMove={handleMove}
		    onMouseUp={handleMouseUp} // You can keep this as is for desktop
		    
		    onTouchMove={handleMove}
		  >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="entryTimestampFormatted" />
          <YAxis />
          <Tooltip />
          <Legend />
          <Bar dataKey="entryTotal" fill="#8884d8" name="Rainfall Total (mm)" />
          {dragStart !== null && dragEnd !== null && (
            <ReferenceArea
              x1={dragStart}
              x2={dragEnd}
              fill="rgba(0, 128, 128, 0.45)"
            />
          )}
        </BarChart>
      </ResponsiveContainer>
      <span className="d-none d-md-inline"><i><b>You can highlight an area of the chart to see a summary</b></i>.<br />Click once to start your selection, move your mouse to adjust the area, then click again to finalize the selected range.</span>
      <span className="d-md-none"><i><b>You can highlight an area of the chart to see a summary</b></i>.<br />Tap once to start your selection, then tap elsewhere to finalize the selected range.</span>
    </div>
  );
}

export default RainfallChart;
